/* Guest wishlist (Dimex) — thin wrapper around the Swym JS SDK.

   Guests get Swym's single default wishlist; logged-in customers keep using the
   cart, so this module is only for the guest path. The contents are rendered by
   tuotelista.js on the PDF-tuotelista page, and product-form.js reads them to
   put add-to-wishlist buttons in their "already saved" state.

   Deliberately uses the default-wishlist API (addToWishList / removeFromWishList
   / fetch) rather than the multi-list API: "multiple-wishlist" is disabled for
   this store, and the default list is what Swym's own UI, admin analytics and
   reminder emails read from.

   Cookie consent for Swym storage is handled by the store's third-party consent
   app and Shopify Admin — this wrapper does not gate on customerPrivacy.

   Docs: https://developers.getswym.com/docs/custom-wishlist-experience-using-js-sdk
         https://developers.getswym.com/docs/wishlist-button-pdp
         https://developers.getswym.com/docs/collection-page-buttons
*/
(function () {
  const SWAT_READY_TIMEOUT_MS = 8000;
  const PRODUCT_DETAILS_TIMEOUT_MS = 2500;
  const CHANGE_EVENT = "dimex:wishlist:change";
  const VALID_SOURCES = {
    "collections-grid": true,
    pdp: true,
    "quick-view": true,
    "featured-grid": true,
    recommendations: true,
    "search-results": true,
    plp: true,
  };

  window.SwymCallbacks = window.SwymCallbacks || [];

  function isGuest() {
    return window.customerLoggedIn === false;
  }

  function withSwat(fn, onTimeout) {
    let settled = false;
    window.SwymCallbacks.push((swat) => {
      if (settled) return;
      settled = true;
      fn(swat);
    });

    if (onTimeout) {
      setTimeout(() => {
        if (settled) return;
        settled = true;
        onTimeout();
      }, SWAT_READY_TIMEOUT_MS);
    }
  }

  /* One shared copy of the list. Every add-to-wishlist button on the page asks
     for its state, and a round trip each would be both slow and pointless: the
     list only changes through this module, so we keep it in step by hand and
     announce it with CHANGE_EVENT. */
  let items = null;
  let itemWaiters = [];
  let fetchStarted = false;
  let pendingAdds = [];
  let pendingRemoves = [];

  function idEq(a, b) {
    return Number(a) === Number(b);
  }

  function sameItem(a, b) {
    return idEq(a.empi, b.empi) && idEq(a.epi, b.epi);
  }

  function takePending(list) {
    let next = list || [];
    pendingRemoves.forEach((product) => {
      next = next.filter((i) => !sameItem(i, product));
    });
    pendingAdds.forEach((product) => {
      if (!next.some((i) => sameItem(i, product)))
        next = next.concat([product]);
    });
    pendingAdds = [];
    pendingRemoves = [];
    return next;
  }

  function setItems(nextItems) {
    items = nextItems || [];
    const waiters = itemWaiters;
    itemWaiters = [];
    waiters.forEach((w) => w.onResult(items));
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { items } }),
    );
  }

  // Nothing is cached on failure, so the next caller retries the fetch.
  function failItems(error) {
    fetchStarted = false;
    const waiters = itemWaiters;
    itemWaiters = [];
    waiters.forEach((w) => (w.onError ? w.onError(error) : w.onResult([])));
  }

  /* swat.fetch returns the default list newest-first (cts desc). The tuotelista
     page renders in addition order — first saved at the top — so we flip it
     here. rememberAdd appends, which then stays consistent for the page life. */
  function inAdditionOrder(list) {
    const items = (list || []).slice();
    if (!items.length) return items;
    const dated = items.every((i) => i && Number.isFinite(Number(i.cts)));
    if (dated) {
      items.sort((a, b) => Number(a.cts) - Number(b.cts));
      return items;
    }
    return items.reverse();
  }

  /* swat.fetch returns the default list's items directly. fetchLists would also
     work and would hand back the list id, but we have no use for the id and it
     is the multi-list entry point — no reason to lean on a disabled feature. */
  function loadItems() {
    withSwat(
      (swat) =>
        swat.fetch(
          (fetched) => setItems(takePending(inAdditionOrder(fetched))),
          failItems,
        ),
      () => failItems(new Error("swym_unavailable")),
    );
  }

  function getItems(onResult, onError) {
    if (items) {
      onResult(items);
      return;
    }
    itemWaiters.push({ onResult, onError });
    if (fetchStarted) return;
    fetchStarted = true;
    loadItems();
  }

  function productHandle(product) {
    if (!product || !product.du) return "";
    let path = product.du;
    try {
      path = new URL(product.du, window.location.origin).pathname;
    } catch (e) {
      /* keep the raw string and parse a /products/ segment out of it */
    }
    const match = String(path).match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }

  function canonicalProductUrl(handle, fallbackDu) {
    if (!handle) return fallbackDu || "";
    let origin = window.location.origin;
    if (fallbackDu) {
      try {
        origin = new URL(fallbackDu, window.location.origin).origin;
      } catch (e) {
        /* keep window.location.origin */
      }
    }
    return origin + "/products/" + handle;
  }

  function inferSource() {
    const path = window.location.pathname;
    if (/\/products\//.test(path)) return "pdp";
    if (/\/collections\//.test(path)) return "collections-grid";
    if (/\/search/.test(path)) return "search-results";
    return "plp";
  }

  function withCanonicalFields(product, productJson) {
    const handle = productHandle(product);
    const resolved = Object.assign({}, product, {
      du: canonicalProductUrl(handle, product.du),
    });

    const source = VALID_SOURCES[product.source] ? product.source : inferSource();
    resolved.source = source;

    if (productJson) {
      if (productJson.empi || productJson.id) {
        resolved.empi = productJson.empi || productJson.id;
      }
      const swymEpi =
        productJson.epi ||
        (productJson.variants && productJson.variants[0] && productJson.variants[0].id);
      if (swymEpi) {
        resolved.epi = swymEpi;
      }
    }

    return resolved;
  }

  /* Swym's collection implementation looks the product up by /products/HANDLE
     before addToWishList. That is what supplies a catalog-known empi/epi;
     sending Liquid IDs alone is the documented sw-unknown-product cause when
     they do not match Swym's snapshot. If the lookup fails or times out, add
     still goes out with a locale-free du so a catalog miss is not also a URL
     miss. */
  function resolveForAdd(swat, product, onResolved) {
    const fallback = withCanonicalFields(product);
    const handle = productHandle(product);

    if (!handle || typeof swat.getProductDetails !== "function") {
      onResolved(fallback);
      return;
    }

    let settled = false;
    const finish = (productJson) => {
      if (settled) return;
      settled = true;
      onResolved(withCanonicalFields(product, productJson));
    };

    setTimeout(() => finish(null), PRODUCT_DETAILS_TIMEOUT_MS);

    try {
      swat.getProductDetails({ du: "/products/" + handle }, (productJson) => {
        finish(productJson);
      }, (error) => {
        console.error("[Wishlist] getProductDetails failed:", error);
        finish(null);
      });
    } catch (e) {
      finish(null);
    }
  }

  function rememberAdd(product) {
    if (items) {
      if (!items.some((i) => sameItem(i, product))) {
        setItems(items.concat([product]));
      }
    } else {
      pendingAdds.push(product);
    }
  }

  function rememberRemove(product) {
    if (items) {
      setItems(items.filter((i) => !sameItem(i, product)));
    } else {
      pendingRemoves.push(product);
    }
  }

  function addToWishlist(product, onSuccess, onError) {
    withSwat(
      (swat) =>
        resolveForAdd(swat, product, (resolved) => {
          swat.addToWishList(
            resolved,
            (response) => {
              rememberAdd(resolved);
              if (onSuccess) onSuccess(response);
            },
            onError,
          );
        }),
      () => onError && onError(new Error("swym_unavailable")),
    );
  }

  function removeFromWishlist(product, onSuccess, onError) {
    withSwat(
      (swat) =>
        swat.removeFromWishList(
          product,
          (response) => {
            rememberRemove(product);
            if (onSuccess) onSuccess(response);
          },
          onError,
        ),
      () => onError && onError(new Error("swym_unavailable")),
    );
  }

  function fetchWishlist(onSuccess, onError) {
    getItems(onSuccess, onError);
  }

  /* Product-level, not variant-level, on purpose: the wishlist page collapses
     items by product id, so a product saved as one variant must not look
     unsaved on a page showing another. */
  function isWishlisted(product, onResult) {
    getItems(
      (current) => onResult(current.some((i) => idEq(i.empi, product.empi))),
      () => onResult(false),
    );
  }

  window.DimexWishlist = {
    CHANGE_EVENT,
    isGuest,
    addToWishlist,
    removeFromWishlist,
    fetchWishlist,
    isWishlisted,
  };
})();
